/**
 * Capstone Project Detail Page
 * Workspace for managing individual capstone project
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  MenuBook as CaseStudyIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import capstoneService from '../services/capstoneService';
import { CapstoneProject, ModuleStatus, CapstoneModule, CapstoneDeliverable } from '../types/capstone';
import { ModuleEditDialog, DeliverableEditDialog, ProjectInfoEditDialog } from '../components/capstone/EditDialogs';
import { TeamEditDialog } from '../components/capstone/TeamEditDialog';
import { CaseStudyEditDialog } from '../components/capstone/CaseStudyEditDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CapstoneProjectDetail: React.FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<CapstoneProject | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  // Edit dialog states
  const [moduleEditOpen, setModuleEditOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<CapstoneModule | null>(null);
  const [deliverableEditOpen, setDeliverableEditOpen] = useState(false);
  const [selectedDeliverable, setSelectedDeliverable] = useState<CapstoneDeliverable | null>(null);
  const [projectInfoEditOpen, setProjectInfoEditOpen] = useState(false);
  const [teamEditOpen, setTeamEditOpen] = useState(false);
  const [caseStudyEditOpen, setCaseStudyEditOpen] = useState(false);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<any>(null);

  useEffect(() => {
    console.log('=== CapstoneProjectDetail Debug ===');
    console.log('projectId from useParams:', projectId);
    console.log('projectId type:', typeof projectId);
    console.log('window.location.pathname:', window.location.pathname);
    console.log('===================================');
    
    if (projectId && projectId !== '{projectId}' && projectId !== 'undefined') {
      loadProject();
    } else {
      setError(`Invalid project ID received: "${projectId}". Please return to the projects list and try again.`);
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProject = async () => {
    if (!projectId || projectId === '{projectId}' || projectId === 'undefined') {
      setError('Invalid project ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('Calling capstoneService.getProjectById with:', projectId);
      const data = await capstoneService.getProjectById(projectId);
      console.log('Project data received:', data?.projectName);
      setProject(data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading project:', err);
      setError('Failed to load project details. Please check the console for more information.');
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/capstone');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getModuleStatusColor = (status: ModuleStatus): string => {
    switch (status) {
      case ModuleStatus.NOT_STARTED:
        return '#9e9e9e';
      case ModuleStatus.IN_PROGRESS:
        return '#2196f3';
      case ModuleStatus.COMPLETED:
        return '#4caf50';
      default:
        return '#9e9e9e';
    }
  };

  // Edit handlers
  const handleEditModule = (module: CapstoneModule) => {
    setSelectedModule(module);
    setModuleEditOpen(true);
  };

  const handleSaveModule = async (moduleData: Partial<CapstoneModule>) => {
    if (!project || !selectedModule) return;
    
    try {
      console.log('=== Module Save Debug ===');
      console.log('moduleData received:', moduleData);
      console.log('selectedModule:', selectedModule);
      
      // Update module in the project
      const updatedModules = project.modules.map(m =>
        m.moduleNumber === selectedModule.moduleNumber
          ? { ...m, ...moduleData }
          : m
      );
      
      console.log('Updated module:', updatedModules.find(m => m.moduleNumber === selectedModule.moduleNumber));
      console.log('Sending to API:', { modules: updatedModules });
      
      // Save to database
      await capstoneService.updateProject(project.projectId, { modules: updatedModules });
      
      // Reload project from database to get the saved data
      const refreshedProject = await capstoneService.getProjectById(project.projectId);
      setProject(refreshedProject);
      setModuleEditOpen(false);
    } catch (error) {
      console.error('Error updating module:', error);
    }
  };

  const handleEditDeliverable = (deliverable: CapstoneDeliverable) => {
    setSelectedDeliverable(deliverable);
    setDeliverableEditOpen(true);
  };

  const handleSaveDeliverable = async (deliverableData: Partial<CapstoneDeliverable>) => {
    if (!project || !selectedDeliverable) return;
    
    try {
      // Update deliverable in the project
      const updatedModules = project.modules.map(module => ({
        ...module,
        deliverables: module.deliverables.map(d =>
          d.deliverableId === selectedDeliverable.deliverableId
            ? { ...d, ...deliverableData }
            : d
        )
      }));
      
      // Save to database
      await capstoneService.updateProject(project.projectId, { modules: updatedModules });
      
      // Reload project from database to get the saved data
      const refreshedProject = await capstoneService.getProjectById(project.projectId);
      setProject(refreshedProject);
      setDeliverableEditOpen(false);
    } catch (error) {
      console.error('Error updating deliverable:', error);
    }
  };

  const handleEditProjectInfo = () => {
    setProjectInfoEditOpen(true);
  };

  const handleEditTeam = () => {
    setTeamEditOpen(true);
  };

  const handleEditCaseStudy = (caseStudy: any) => {
    setSelectedCaseStudy(caseStudy);
    setCaseStudyEditOpen(true);
  };

  const handleSaveCaseStudy = async (caseStudyData: any) => {
    if (!project || !selectedCaseStudy) {
      console.error('Cannot save: project or selectedCaseStudy is null', { project, selectedCaseStudy });
      return;
    }
    
    try {
      console.log('=== Case Study Save Debug ===');
      console.log('Selected case study:', selectedCaseStudy);
      console.log('Saving case study data:', caseStudyData);
      console.log('Current project case studies:', project.caseStudies);
      
      // Find and update the case study in the array
      const updatedCaseStudies = project.caseStudies?.map((cs: any) => {
        // Match by organization name or _id if available
        const isMatch = cs._id ? cs._id === selectedCaseStudy._id : cs.organization === selectedCaseStudy.organization;
        if (isMatch) {
          console.log('Found matching case study, updating:', cs);
          // Preserve the _id if it exists
          return { ...cs, ...caseStudyData, _id: cs._id };
        }
        return cs;
      }) || [];
      
      console.log('Updated case studies array:', updatedCaseStudies);
      
      // Save to database
      console.log('Calling API to update project...');
      const response = await capstoneService.updateProject(project.projectId, { caseStudies: updatedCaseStudies });
      console.log('API response:', response);
      console.log('Save complete, reloading project...');
      
      // Reload project from database to get the saved data
      const refreshedProject = await capstoneService.getProjectById(project.projectId);
      console.log('Refreshed project:', refreshedProject);
      console.log('Refreshed case studies:', refreshedProject.caseStudies);
      setProject(refreshedProject);
      setCaseStudyEditOpen(false);
      setSelectedCaseStudy(null);
    } catch (error) {
      console.error('Error updating case study:', error);
      alert('Failed to save case study. Check console for details.');
    }
  };

  const handleSaveTeam = async (teamData: any) => {
    if (!project) return;
    
    try {
      console.log('=== Team Save Debug ===');
      console.log('Saving team data:', teamData);
      
      // Save to database
      await capstoneService.updateProject(project.projectId, { team: teamData });
      console.log('Save complete, reloading project...');
      
      // Reload project from database to get the saved data
      const refreshedProject = await capstoneService.getProjectById(project.projectId);
      console.log('Refreshed team:', refreshedProject.team);
      setProject(refreshedProject);
      setTeamEditOpen(false);
    } catch (error) {
      console.error('Error updating team:', error);
    }
  };

  const handleSaveProjectInfo = async (projectData: any) => {
    if (!project) return;
    
    try {
      console.log('=== Project Info Save Debug ===');
      console.log('Saving project data:', projectData);
      
      // Save to database
      await capstoneService.updateProject(project.projectId, projectData);
      console.log('Save complete, reloading project...');
      
      // Reload project from database to get the saved data
      const refreshedProject = await capstoneService.getProjectById(project.projectId);
      console.log('Refreshed project name:', refreshedProject.projectName);
      setProject(refreshedProject);
      setProjectInfoEditOpen(false);
    } catch (error) {
      console.error('Error updating project info:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Back to Projects
        </Button>
        <Box sx={{ flexGrow: 1 }} key={project?.projectName}>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            {project?.projectName || 'Capstone Project'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {project?.uscisUseCase || 'USCIS Use Case'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={handleEditProjectInfo}
          sx={{ borderColor: 'primary.main', color: 'primary.main' }}
        >
          Edit Project
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Error Loading Project</Typography>
          <Typography variant="body2">{error}</Typography>
          <Button 
            variant="outlined" 
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Return to Projects List
          </Button>
        </Alert>
      )}

      {/* Project Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Overall Progress
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {project ? Math.round(project.modules.reduce((sum, m) => sum + m.completionPercentage, 0) / project.modules.length) : 0}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={project ? Math.round(project.modules.reduce((sum, m) => sum + m.completionPercentage, 0) / project.modules.length) : 0}
                sx={{ mt: 2, height: 8, borderRadius: 4 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Modules Completed
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                {project ? project.modules.filter(m => m.status === ModuleStatus.COMPLETED).length : 0} / 7
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {project ? project.modules.filter(m => m.status === ModuleStatus.IN_PROGRESS).length : 0} in progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Time Invested
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                {project?.metadata.totalHoursInvested || 0}h
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Target: {project?.timeline.estimatedHoursPerWeek || 5}h/week
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Target Completion
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                {project ? new Date(project.timeline.targetCompletionDate).toLocaleDateString() : 'N/A'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {project ? Math.ceil((new Date(project.timeline.targetCompletionDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 0} days remaining
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="project detail tabs">
          <Tab label="Overview" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Modules (7)" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Case Studies" icon={<CaseStudyIcon />} iconPosition="start" />
          <Tab label="Team" icon={<PeopleIcon />} iconPosition="start" />
          <Tab label="Final Presentation" icon={<TrophyIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <OverviewTab project={project} onEditProject={handleEditProjectInfo} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ModulesTab 
          project={project} 
          getModuleStatusColor={getModuleStatusColor}
          onEditModule={handleEditModule}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <CaseStudiesTab project={project} onEditCaseStudy={handleEditCaseStudy} />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <TeamTab project={project} onEditTeam={handleEditTeam} />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <FinalPresentationTab project={project} />
      </TabPanel>

      {/* Edit Dialogs */}
      <ModuleEditDialog
        open={moduleEditOpen}
        module={selectedModule}
        onClose={() => setModuleEditOpen(false)}
        onSave={handleSaveModule}
      />
      
      <DeliverableEditDialog
        open={deliverableEditOpen}
        deliverable={selectedDeliverable}
        onClose={() => setDeliverableEditOpen(false)}
        onSave={handleSaveDeliverable}
      />
      
      <ProjectInfoEditDialog
        open={projectInfoEditOpen}
        project={project}
        onClose={() => setProjectInfoEditOpen(false)}
        onSave={handleSaveProjectInfo}
      />
      
      <TeamEditDialog
        open={teamEditOpen}
        project={project}
        onClose={() => setTeamEditOpen(false)}
        onSave={handleSaveTeam}
      />
      
      <CaseStudyEditDialog
        open={caseStudyEditOpen}
        caseStudy={selectedCaseStudy}
        onClose={() => setCaseStudyEditOpen(false)}
        onSave={handleSaveCaseStudy}
      />
    </Container>
  );
};

// Overview Tab Component
const OverviewTab: React.FC<{ 
  project: CapstoneProject | null;
  onEditProject: () => void;
}> = ({ project, onEditProject }) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,51,102,0.15)',
              borderColor: 'primary.main'
            },
            border: '2px solid transparent'
          }}
          onClick={onEditProject}
          role="button"
          tabIndex={0}
          aria-label="Click to edit project information"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onEditProject();
            }
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              Project Description
            </Typography>
            <Typography variant="body1" paragraph>
              {project?.description || 'No description available.'}
            </Typography>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              Timeline
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Start Date</Typography>
                <Typography variant="body1">{project ? new Date(project.timeline.startDate).toLocaleDateString() : 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Target Completion</Typography>
                <Typography variant="body1">{project ? new Date(project.timeline.targetCompletionDate).toLocaleDateString() : 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Hours/Week</Typography>
                <Typography variant="body1">{project?.timeline.estimatedHoursPerWeek || 5} hours</Typography>
              </Box>
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Click anywhere to edit
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button variant="outlined" fullWidth>Continue Learning</Button>
              <Button variant="outlined" fullWidth>View Templates</Button>
              <Button variant="outlined" fullWidth>Request Peer Review</Button>
              <Button variant="outlined" fullWidth>Export Progress</Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Modules Tab Component
const ModulesTab: React.FC<{ 
  project: CapstoneProject | null; 
  getModuleStatusColor: (status: ModuleStatus) => string;
  onEditModule: (module: CapstoneModule) => void;
}> = ({ project, getModuleStatusColor, onEditModule }) => {
  return (
    <Grid container spacing={3}>
      {project?.modules.map((module) => {
        return (
          <Grid item xs={12} md={6} key={module.moduleNumber}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,51,102,0.15)',
                  borderColor: 'primary.main'
                },
                border: '2px solid transparent'
              }}
              onClick={() => onEditModule(module)}
              role="button"
              tabIndex={0}
              aria-label={`Click to edit ${module.moduleName}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onEditModule(module);
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Module {module.moduleNumber}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {module.moduleName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                      label={module?.status || ModuleStatus.NOT_STARTED}
                      size="small"
                      sx={{
                        backgroundColor: getModuleStatusColor(module?.status || ModuleStatus.NOT_STARTED),
                        color: 'white'
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        module && onEditModule(module);
                      }}
                      aria-label="Edit module"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                {/* Learning Key Points */}
                {module.moduleDescription && (
                  <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'block', mb: 0.5 }}>
                      Learning Key Points
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {module.moduleDescription}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Progress</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {module?.completionPercentage || 0}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={module?.completionPercentage || 0}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Click anywhere to edit
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

// Team Tab Component
const TeamTab: React.FC<{ 
  project: CapstoneProject | null;
  onEditTeam: () => void;
}> = ({ project, onEditTeam }) => {
  return (
    <Grid container spacing={3}>
      {/* Team Members Section */}
      <Grid item xs={12} md={6}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,51,102,0.15)',
              borderColor: 'primary.main'
            },
            border: '2px solid transparent'
          }}
          onClick={onEditTeam}
          role="button"
          tabIndex={0}
          aria-label="Click to edit team members"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onEditTeam();
            }
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Team Members
              </Typography>
              <Chip 
                label={project?.team.members.length || 0} 
                sx={{ backgroundColor: 'primary.main', color: 'white' }}
              />
            </Box>
            
            {project?.team.members && project.team.members.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {project.team.memberNames?.map((name, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      {name.charAt(0).toUpperCase()}
                    </Box>
                    <Typography variant="body2">{name}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No team members yet
              </Typography>
            )}
            
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 2 }}>
              Click anywhere to edit
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Peer Reviewers Section */}
      <Grid item xs={12} md={6}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,51,102,0.15)',
              borderColor: 'primary.main'
            },
            border: '2px solid transparent'
          }}
          onClick={onEditTeam}
          role="button"
          tabIndex={0}
          aria-label="Click to edit peer reviewers"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onEditTeam();
            }
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Peer Reviewers
              </Typography>
              <Chip 
                label={project?.team.peerReviewers?.length || 0} 
                sx={{ backgroundColor: '#4caf50', color: 'white' }}
              />
            </Box>
            
            {project?.team.peerReviewers && project.team.peerReviewers.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {project.team.peerReviewerNames?.map((name, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      {name.charAt(0).toUpperCase()}
                    </Box>
                    <Typography variant="body2">{name}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No peer reviewers assigned
              </Typography>
            )}
            
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 2 }}>
              Click anywhere to edit
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Collaboration Guidelines */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              Collaboration Guidelines
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Team Members:</strong> Collaborate on deliverables, share insights, and support each other through the capstone journey.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Peer Reviewers:</strong> Provide constructive feedback on deliverables to help improve quality and ensure alignment with best practices.
            </Typography>
            <Typography variant="body2">
              <strong>Best Practices:</strong> Schedule regular check-ins, document decisions, share resources, and maintain open communication throughout the project.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Case Studies Tab Component
const CaseStudiesTab: React.FC<{ 
  project: CapstoneProject | null;
  onEditCaseStudy: (caseStudy: any) => void;
}> = ({ project, onEditCaseStudy }) => {
  // Use case studies from project, or fallback to default if not available
  const caseStudies = project?.caseStudies || [
    {
      organization: 'Majid Al Futtaim',
      focus: 'Data Governance & Growth',
      insights: 'Analytics-driven transformation in MENA region mitigating pandemic impacts',
      color: '#003366'
    },
    {
      organization: 'IBM Watson',
      focus: 'AI in Healthcare',
      insights: 'Balancing AI promise with healthcare market realities',
      color: '#1976d2'
    },
    {
      organization: 'RIMAC',
      focus: 'Enterprise AI',
      insights: 'Scaling AI across an organization amid cultural challenges',
      color: '#0288d1'
    },
    {
      organization: 'ShotSpotter',
      focus: 'Ethical Data Innovation',
      insights: 'Using AI and IoT to address urban governance and social issues',
      color: '#0097a7'
    }
  ];

  return (
    <Grid container spacing={3}>
      {caseStudies.map((study, index) => (
        <Grid item xs={12} md={6} key={index}>
          <Card
            sx={{
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0,51,102,0.15)',
                borderColor: study.color
              },
              border: '2px solid transparent'
            }}
            onClick={() => onEditCaseStudy(study)}
            role="button"
            tabIndex={0}
            aria-label={`Click to edit ${study.organization} case study`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onEditCaseStudy(study);
              }
            }}
          >
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: study.color,
                    mb: 1
                  }}
                >
                  {study.organization}
                </Typography>
                <Chip 
                  label={study.focus} 
                  sx={{ 
                    backgroundColor: study.color,
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    display: 'block',
                    mb: 1
                  }}
                >
                  Key Insights
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {study.insights}
                </Typography>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Click anywhere to edit
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// Final Presentation Tab Component
const FinalPresentationTab: React.FC<{ project: CapstoneProject | null }> = ({ project }) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info">
          Final presentation builder will be available after completing all 7 modules.
        </Alert>
      </Grid>
    </Grid>
  );
};

export default CapstoneProjectDetail;
